import { MAX_MUTE_MINUTES, MAX_NAME_PART_BYTES, MUTE_ALL, chatText, isChatChannel, type ChatChannel, type ChatTextProblem, type MuteScope } from "odyssey-shared";
import { partyRequestOf } from "../party/commands.ts";
import { BADGE_COMMAND_WORD, readBadgeCommand, type BadgeAsk } from "../badge/command.ts";
import { gmCommandNamed, readModerationCommand, type ModerationAsk } from "../moderation/command.ts";
import { commandNamed, type ChatCommand } from "./commands.ts";
import type { PartyRequest } from "../party/members.ts";

/**
 * The chat input's text as a request (ADR-068 decision 8 and its amendment "chat commands, Discord style"), read
 * on the page before anything is sent: plain text is a Local line; `/w First Family text` (or `/whisper`) a
 * whisper to a full name; `/re text` (or `/reply`, `/^`) a reply to the newest player who whispered, whom the store
 * finds; `/me text` a roleplay action to Local; `/block Family` and `/unblock Family`, the family alone or a full
 * name's last part, a change to the block list; `/blocks` asks for the list, which the page already holds; `/ping`
 * measures the round trip to the server, which the store runs (ADR-068's amendment "`/ping`"); `/g`, `/t` and `/h`
 * a line to Global, Trade or Help, `/p` a line to Party (ADR-101 decision 25), `/join` and `/leave` a channel by its word
 * (`/leave party` leaves the party), `/invite`, `/remove`, `/promote` and `/grant` a party request naming a full name (ADR-101 decisions 3, 4 and 9), and a GM's `/mute Family 30m
 * [channel]` and `/unmute Family [channel]` (its amendment "Global, Trade and Help"). Every other command of the map
 * is reserved and answered closed, never sent. A word the map does not name may be one of a GM's moderation commands
 * (ADR-056's amendment of 2026-10-04, `moderation/command.ts`), which the map leaves out, since only a page the
 * gateway offered them to acts on one; every other page answers it as it does any word that is no command. Commands are read whole and without regard
 * to case. Every text is held to the chat text rule the server applies, so the page refuses what the server would,
 * and says why, without sending.
 */

/**
 * Why the page refuses an input without sending it: the text rule's reasons; a slash naming no command; a
 * whisper naming no full name; a block or an unblock naming no family; a reply with no one to reach; a join or a
 * leave naming no channel; a mute or an unmute out of its syntax.
 */
export type PageRefusal = ChatTextProblem | "command" | "target" | "family" | "nobody" | "channel" | "mute" | "name";

/**
 * What an input asks for.
 */
export type ChatIntent =
  | { readonly kind: "say"; readonly text: string }
  | { readonly kind: "pose"; readonly text: string }
  | { readonly kind: "whisper"; readonly firstName: string; readonly familyName: string; readonly text: string }
  | { readonly kind: "reply"; readonly text: string }
  | { readonly kind: "block"; readonly familyName: string }
  | { readonly kind: "unblock"; readonly familyName: string }
  | { readonly kind: "blocks" }
  | { readonly kind: "clear" }
  | { readonly kind: "ping" }
  | { readonly kind: "afk" }
  | { readonly kind: "channel"; readonly command: ChatCommand; readonly channel: ChatChannel; readonly text: string }
  | { readonly kind: "party"; readonly text: string }
  | { readonly kind: "membership"; readonly command: ChatCommand; readonly channel: ChatChannel; readonly joined: boolean }
  | { readonly kind: "mute"; readonly familyName: string; readonly channel: MuteScope; readonly minutes: number }
  | { readonly kind: "partyRequest"; readonly request: PartyRequest }
  | { readonly kind: "moderation"; readonly ask: ModerationAsk }
  | { readonly kind: "badge"; readonly ask: BadgeAsk }
  | { readonly kind: "closed"; readonly command: ChatCommand }
  | { readonly kind: "leave" }
  | { readonly kind: "refused"; readonly reason: PageRefusal };

/**
 * A part of a name as a command names it: the characters every name admits, at most the wire's 32.
 */
const NAME_PART = new RegExp(`^[A-Za-z0-9_-]{1,${MAX_NAME_PART_BYTES}}$`);

/**
 * White space between a command's words.
 */
const SPACE = /\s+/;

/**
 * A command's word and what follows it: the word, the white space after it, and the rest as typed.
 */
const COMMAND = /^\/(\S*)\s*([\s\S]*)$/;

/**
 * A whisper's two names and its text as typed after them.
 */
const WHISPER = /^(\S+)\s+(\S+)(?:\s+([\s\S]*))?$/;

/**
 * A mute's duration: a whole number, then `m` for minutes (the default), `h` for hours or `d` for days.
 */
const DURATION = /^(\d{1,5})([mhd]?)$/i;

/**
 * The minutes each unit of a duration holds.
 */
const UNIT_MINUTES: Readonly<Record<string, number>> = { "": 1, m: 1, h: 60, d: 24 * 60 };

/**
 * The channels the commands that name one take, as words: the three channel words, read without regard to case.
 *
 * @param word The word as typed.
 * @returns The channel, or null when the word names none.
 */
function channelNamed(word: string): ChatChannel | null {
  const lower = word.toLowerCase();
  return isChatChannel(lower) ? lower : null;
}

/**
 * A mute's minutes as typed.
 *
 * @param word The duration as typed.
 * @returns Its minutes, 1 to `MAX_MUTE_MINUTES`, or null when it is out of the syntax or the bound.
 */
function muteMinutes(word: string): number | null {
  const parts = DURATION.exec(word);
  if (parts === null) {
    return null;
  }
  const minutes = Number(parts[1]) * (UNIT_MINUTES[(parts[2] ?? "").toLowerCase()] ?? 1);
  return minutes >= 1 && minutes <= MAX_MUTE_MINUTES ? minutes : null;
}

/**
 * A GM's mute or unmute: a family, a duration for a mute, and a channel or none for all three.
 *
 * @param rest What follows the command.
 * @param lift True for `/unmute`, which takes no duration.
 * @returns The mute, or the page's refusal.
 */
function mute(rest: string, lift: boolean): ChatIntent {
  const words = rest.trim().split(SPACE).filter((word) => word !== "");
  const [family, ...after] = words;
  const minutes = lift ? 0 : muteMinutes(after.shift() ?? "");
  const scope: MuteScope | null = after.length === 0 ? MUTE_ALL : after.length === 1 ? channelNamed(after[0] ?? "") : null;
  if (family === undefined || !NAME_PART.test(family) || minutes === null || scope === null) {
    return { kind: "refused", reason: "mute" };
  }
  return { kind: "mute", familyName: family, channel: scope, minutes };
}

/**
 * The text rule over a text, as an intent.
 *
 * @param text The text.
 * @param make The intent for a text the rule admits.
 * @returns The intent, or the rule's refusal.
 */
function checked(text: string, make: (text: string) => ChatIntent): ChatIntent {
  const verdict = chatText(text);
  return verdict.kind === "ok" ? make(verdict.text) : { kind: "refused", reason: verdict.reason };
}

/**
 * The family a block or an unblock names: one word, or two, a full name, whose last is the family.
 *
 * @param rest What follows the command.
 * @returns The family name, or null when the words name none.
 */
function namedFamily(rest: string): string | null {
  const words = rest.trim().split(SPACE).filter((word) => word !== "");
  const family = words.at(-1);
  return words.length >= 1 && words.length <= 2 && words.every((word) => NAME_PART.test(word)) && family !== undefined ? family : null;
}

/**
 * A whisper's full name and its text.
 *
 * @param rest What follows the command.
 * @returns The whisper, or the page's refusal.
 */
function whisper(rest: string): ChatIntent {
  const parts = WHISPER.exec(rest);
  const first = parts?.[1];
  const family = parts?.[2];
  if (first === undefined || family === undefined || !NAME_PART.test(first) || !NAME_PART.test(family)) {
    return { kind: "refused", reason: "target" };
  }
  return checked(parts?.[3] ?? "", (text) => ({ kind: "whisper", firstName: first, familyName: family, text }));
}

/**
 * What an open command asks for.
 *
 * @param command The command.
 * @param rest What follows its word.
 * @returns The intent.
 */
function openCommand(command: ChatCommand, rest: string): ChatIntent {
  switch (command.id) {
    case "whisper":
      return whisper(rest);
    case "reply":
      return checked(rest, (text) => ({ kind: "reply", text }));
    case "pose":
      return checked(rest, (text) => ({ kind: "pose", text }));
    case "block":
    case "unblock": {
      const family = namedFamily(rest);
      if (family === null) {
        return { kind: "refused", reason: "family" };
      }
      return command.id === "block" ? { kind: "block", familyName: family } : { kind: "unblock", familyName: family };
    }
    case "ping":
      return { kind: "ping" };
    case "afk":
      return { kind: "afk" };
    case "clear":
      return { kind: "clear" };
    case "global":
    case "trade":
    case "help": {
      const channel = command.id;
      return checked(rest, (text) => ({ kind: "channel", command, channel, text }));
    }
    case "party":
      return checked(rest, (text) => ({ kind: "party", text }));
    case "invite":
    case "remove":
    case "promote":
    case "grant": {
      const request = partyRequestOf(command.id, rest);
      return request === null ? { kind: "refused", reason: "name" } : { kind: "partyRequest", request };
    }
    case "join":
    case "leave": {
      const words = rest.trim().split(SPACE).filter((word) => word !== "");
      if (command.id === "leave" && words.length === 1 && words[0]?.toLowerCase() === "party") {
        return { kind: "partyRequest", request: { kind: "partyLeave" } };
      }
      const channel = words.length === 1 ? channelNamed(words[0] ?? "") : null;
      return channel === null ? { kind: "refused", reason: "channel" } : { kind: "membership", command, channel, joined: command.id === "join" };
    }
    case "mute":
    case "unmute":
      return mute(rest, command.id === "unmute");
    default:
      return { kind: "blocks" };
  }
}

/**
 * Reads what the chat input holds.
 *
 * @param draft The input's text.
 * @returns What it asks for.
 * @example
 * readChatInput("/w Kaelen Ashgrove are you there?");
 * // { kind: "whisper", firstName: "Kaelen", familyName: "Ashgrove", text: "are you there?" }
 * readChatInput("/g hello");
 * // { kind: "closed", command: the Global command }
 */
export function readChatInput(draft: string): ChatIntent {
  const typed = draft.trim();
  if (typed === "") {
    return { kind: "leave" };
  }
  const parts = COMMAND.exec(typed);
  if (parts === null) {
    return checked(typed, (text) => ({ kind: "say", text }));
  }
  const command = commandNamed(parts[1] ?? "");
  if (command === undefined) {
    if ((parts[1] ?? "").toLowerCase() === BADGE_COMMAND_WORD) {
      return { kind: "badge", ask: readBadgeCommand(parts[2] ?? "") };
    }
    const gm = gmCommandNamed(parts[1] ?? "");
    return gm === null ? { kind: "refused", reason: "command" } : { kind: "moderation", ask: readModerationCommand(gm, parts[2] ?? "") };
  }
  return command.open ? openCommand(command, parts[2] ?? "") : { kind: "closed", command };
}
